/* @flow */
import * as React from "react";

type BackButtonResponderCallback = () => { handled: boolean };
type ResponderCallbackManager = {
  unregister: () => void,
  replace: (BackButtonResponderCallback) => void,
};

export class BackButtonListener {
  constructor() {
    this._responderCallbacks = [];
  }

  static _singleton: ?BackButtonListener;

  static setSingleton(instance: BackButtonListener | null) {
    this._singleton = instance;
  }

  static getSingleton(): ?BackButtonListener {
    return this._singleton;
  }

  _responderCallbacks: Array<BackButtonResponderCallback>;

  processBackButtonPress() {
    this._callResponderCallbacks();
  }

  startListening() {
    // This body checks is to make flow happy...
    if (document.body == null) {
      return;
    }
    document.body.addEventListener("keyup", this._handleKeyUp);
  }

  stopListening() {
    // This body checks is to make flow happy...
    if (document.body == null) {
      return;
    }
    document.body.removeEventListener("keyup", this._handleKeyUp);
  }

  registerResponderCallback(
    responderCallback: BackButtonResponderCallback
  ): ResponderCallbackManager {
    this._responderCallbacks.push(responderCallback);

    let referenceResponderCallback = responderCallback;
    const getCallbackIndex = () => {
      return this._responderCallbacks.indexOf(referenceResponderCallback);
    };

    return {
      unregister: () => {
        const index = getCallbackIndex();
        if (index === -1) {
          throw new Error(
            "Attempted to remove a back button responder callback that had already been removed."
          );
        } else {
          this._responderCallbacks.splice(index, 1);
        }
      },
      replace: (newResponderCallback: BackButtonResponderCallback) => {
        const index = getCallbackIndex();
        if (index === -1) {
          throw new Error(
            "Attempted to update a back button responder callback that had been removed."
          );
        } else {
          this._responderCallbacks.splice(index, 1, newResponderCallback);
          referenceResponderCallback = newResponderCallback;
        }
      },
    };
  }

  _handleKeyUp = (event: KeyboardEvent) => {
    if (event.code !== "Escape") {
      return;
    }

    this._callResponderCallbacks();
  };

  _callResponderCallbacks = () => {
    // Iterate over the array backwards, so that most-recently-added responders
    // are called first.
    for (let i = this._responderCallbacks.length - 1; i >= 0; i--) {
      const callback = this._responderCallbacks[i];

      const { handled } = callback();
      if (handled) {
        break;
      }
    }
  };
}

type Props = {
  onPressed: BackButtonResponderCallback,
};

export default class BackButtonResponder extends React.Component<Props> {
  _responderCallbackManager: ?ResponderCallbackManager;

  componentDidMount() {
    const listener = BackButtonListener.getSingleton();
    if (listener == null) {
      return;
    }

    const { onPressed } = this.props;
    if (onPressed == null) {
      return;
    }

    this._responderCallbackManager = listener.registerResponderCallback(
      onPressed
    );
  }

  componentDidUpdate(prevProps: Props) {
    if (prevProps.onPressed === this.props.onPressed) {
      return;
    }

    const listener = BackButtonListener.getSingleton();
    if (listener == null) {
      return;
    }

    const { onPressed } = this.props;
    if (onPressed == null) {
      return;
    }

    if (this._responderCallbackManager != null) {
      this._responderCallbackManager.replace(onPressed);
    }
  }

  componentWillUnmount() {
    if (this._responderCallbackManager != null) {
      this._responderCallbackManager.unregister();
    }
  }

  render() {
    return null;
  }
}



// WEBPACK FOOTER //
// ../nx-components/src/BackButtonResponder.jsx